import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadBuild } from "../lib/api";
import { useLiveEvents } from "../lib/live";
import type { Build, LiveEvent } from "../lib/types";
import {
  Badge, Button, Card, Empty, ErrorNote, Field, Input, SimulatedNote, Spinner, Status,
} from "../components/ui";
import { bytes, dateTime, duration, relative } from "../lib/format";

type Tab = "overview" | "blueprint" | "build" | "files" | "deploys" | "secrets";

export default function ProjectPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [logLines, setLogLines] = useState<string[]>([]);
  const [liveBuildID, setLiveBuildID] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["project", id],
    queryFn: () => api.projects.get(id),
    enabled: !!id,
  });

  const project = data?.project;
  const latestBuild = data?.builds?.[0];
  const running =
    !!latestBuild &&
    ["queued", "scaffolding", "filling", "verifying"].includes(latestBuild.status);
  const deploying = data?.deployments?.[0]?.status
    ? ["queued", "shipping", "verifying"].includes(data.deployments[0].status)
    : false;

  // Live progress. Build and deploy lines arrive over SSE rather than by
  // polling, so a two-minute build reads like a terminal instead of a spinner.
  const connected = useLiveEvents((ev: LiveEvent) => {
    if (ev.kind === "build.log" || ev.kind === "deploy.log") {
      setLogLines((prev) => {
        const next = [...prev, ev.message ?? ""];
        // Bounded. A pathological build could otherwise grow this array until
        // the tab runs out of memory.
        return next.length > 2000 ? next.slice(-2000) : next;
      });
      return;
    }
    if (ev.kind === "build.status") {
      setLiveBuildID(ev.build_id ?? null);
    }
    // A status change means the server has new truth; refetch rather than
    // trying to reconstruct it from the event.
    void qc.invalidateQueries({ queryKey: ["project", id] });
    void qc.invalidateQueries({ queryKey: ["billing"] });
  });

  // While something is running, poll as a backstop. SSE is the fast path, but
  // a dropped stream must not leave the page frozen on "building" forever.
  useEffect(() => {
    if (!running && !deploying) return;
    const t = setInterval(() => {
      void qc.invalidateQueries({ queryKey: ["project", id] });
    }, 5000);
    return () => clearInterval(t);
  }, [running, deploying, id, qc]);

  // Jump to the build tab when one starts, so the log is what you are looking
  // at rather than something you have to go and find.
  useEffect(() => {
    if (running || deploying) setTab("build");
  }, [running, deploying]);

  const plan = useMutation({
    mutationFn: () => api.projects.plan(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", id] }),
  });
  const build = useMutation({
    mutationFn: () => api.projects.startBuild(id),
    onSuccess: () => {
      setLogLines([]);
      setTab("build");
      void qc.invalidateQueries({ queryKey: ["project", id] });
    },
  });
  const deploy = useMutation({
    mutationFn: () => api.projects.deploy(id),
    onSuccess: () => {
      setLogLines([]);
      setTab("build");
      void qc.invalidateQueries({ queryKey: ["project", id] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!project || !data) return null;

  const passing = data.builds.find((b) => b.status === "passed");
  const tabs: { key: Tab; label: string; badge?: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "blueprint", label: "Blueprint" },
    { key: "build", label: "Build" },
    { key: "files", label: "Files", badge: latestBuild ? String(latestBuild.files_written) : undefined },
    { key: "deploys", label: "Deploys" },
    { key: "secrets", label: "Secrets" },
  ];

  return (
    <div>
      <div className="mb-1 text-xs">
        <Link to="/app" className="text-muted transition hover:text-ink">
          ← Projects
        </Link>
      </div>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">{project.name}</h1>
            <Status value={project.status} />
            {!connected && (running || deploying) && (
              <Badge>reconnecting…</Badge>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted">{project.prompt}</p>
          <p className="mt-1.5 font-mono text-2xs text-muted">{project.slug}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {project.app_url && project.status === "live" && (
            <a href={project.app_url} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm">Open app ↗</Button>
            </a>
          )}
          {!data.blueprint && (
            <Button size="sm" variant="ghost" onClick={() => plan.mutate()} disabled={plan.isPending}>
              {plan.isPending ? "Designing…" : "Design"}
            </Button>
          )}
          <Button size="sm" onClick={() => build.mutate()} disabled={build.isPending || running || !data.blueprint}>
            {running ? "Building…" : build.isPending ? "Starting…" : "Build"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => deploy.mutate()}
            disabled={deploy.isPending || deploying || !passing}
          >
            {deploying ? "Deploying…" : "Deploy"}
          </Button>
        </div>
      </header>

      {(plan.error || build.error || deploy.error) && (
        <div className="mb-4">
          <ErrorNote error={plan.error || build.error || deploy.error} />
        </div>
      )}

      <nav className="mb-5 flex flex-wrap gap-1 border-b border-hairline">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition ${
              tab === t.key ? "border-clay font-medium" : "border-transparent text-muted hover:text-ink"
            }`}
          >
            {t.label}
            {t.badge && <span className="text-2xs text-muted">{t.badge}</span>}
          </button>
        ))}
      </nav>

      {tab === "overview" && <Overview data={data} />}
      {tab === "blueprint" && <BlueprintTab blueprint={data.blueprint} />}
      {tab === "build" && (
        <BuildTab
          builds={data.builds}
          liveLines={logLines}
          liveBuildID={liveBuildID}
          running={running || deploying}
        />
      )}
      {tab === "files" && <FilesTab buildID={latestBuild?.id} slug={project.slug} version={latestBuild?.version} />}
      {tab === "deploys" && <DeploysTab projectID={id} deployments={data.deployments} />}
      {tab === "secrets" && <SecretsTab projectID={id} />}
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────

function Overview({ data }: { data: NonNullable<Awaited<ReturnType<typeof api.projects.get>>> }) {
  const b = data.builds[0];
  const d = data.deployments.find((x) => x.status === "live") ?? data.deployments[0];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-5">
        <h2 className="mb-3 text-sm font-medium">Latest build</h2>
        {!b ? (
          <p className="text-sm text-muted">Nothing built yet.</p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted">Version</dt>
            <dd className="tabular-nums">v{b.version}</dd>
            <dt className="text-muted">Status</dt>
            <dd><Status value={b.status} /></dd>
            <dt className="text-muted">Files</dt>
            <dd className="tabular-nums">
              {b.files_written}
              {b.ai_files > 0 && <span className="text-muted"> · {b.ai_files} by the model</span>}
            </dd>
            <dt className="text-muted">Took</dt>
            <dd className="tabular-nums">{duration(b.started_at, b.finished_at)}</dd>
            <dt className="text-muted">Gates</dt>
            <dd className="flex flex-wrap gap-1">
              <Gate ok={b.go_vet_ok} label="vet" />
              <Gate ok={b.go_build_ok} label="build" />
              <Gate ok={b.go_test_ok} label="test" />
              {(b.ts_check_ok || b.web_build_ok) && <Gate ok={b.ts_check_ok} label="tsc" />}
              {(b.ts_check_ok || b.web_build_ok) && <Gate ok={b.web_build_ok} label="web" />}
              {b.ios_build_ok && <Gate ok label="ios" />}
            </dd>
          </dl>
        )}
        {b?.error && <p className="mt-3 text-xs text-rust">{b.error}</p>}
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 text-sm font-medium">Deployment</h2>
        {!d ? (
          <p className="text-sm text-muted">Not deployed yet.</p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted">Status</dt>
            <dd><Status value={d.status} /></dd>
            <dt className="text-muted">Release</dt>
            <dd className="truncate font-mono text-xs">{d.release_id || "—"}</dd>
            <dt className="text-muted">Health gate</dt>
            <dd>{d.health_ok ? "passed" : "—"}</dd>
            <dt className="text-muted">When</dt>
            <dd>{relative(d.created_at)}</dd>
          </dl>
        )}
        {d?.error && <p className="mt-3 text-xs text-rust">{d.error}</p>}
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 text-sm font-medium">Database</h2>
        {!data.database ? (
          <p className="text-sm text-muted">
            Provisioned on the first deploy — its own database and its own scoped role.
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted">Engine</dt>
            <dd>{data.database.engine}</dd>
            <dt className="text-muted">Name</dt>
            <dd className="truncate font-mono text-xs">{data.database.name}</dd>
            <dt className="text-muted">Host</dt>
            <dd className="truncate font-mono text-xs">{data.database.host || "local"}</dd>
            <dt className="text-muted">Status</dt>
            <dd>{data.database.status}</dd>
          </dl>
        )}
        <p className="mt-3 text-2xs leading-relaxed text-muted">
          The password is sealed at rest and written only into the app's environment file on the fleet host.
          It is never returned by this API.
        </p>
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 text-sm font-medium">Surfaces</h2>
        <div className="flex flex-wrap gap-1.5">
          {data.project.surfaces.split(",").filter(Boolean).map((s) => (
            <Badge key={s}>{s}</Badge>
          ))}
          {data.project.features.split(",").filter(Boolean).map((f) => (
            <Badge key={f} tone="clay">{f}</Badge>
          ))}
        </div>
        <p className="mt-3 text-2xs leading-relaxed text-muted">
          iOS and Android projects are generated from the same blueprint as the backend, so their models
          cannot drift from the wire — but they are not compiled here, because that needs Xcode and the
          Android SDK. Build them locally.
        </p>
      </Card>
    </div>
  );
}

function Gate({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`rounded-full border px-1.5 py-0.5 text-2xs ${
        ok ? "border-moss/40 text-moss" : "border-hairline text-muted"
      }`}
    >
      {label}
    </span>
  );
}

// ─── Blueprint ───────────────────────────────────────────────────────────────

function BlueprintTab({ blueprint }: { blueprint?: import("../lib/types").Blueprint }) {
  if (!blueprint) {
    return <Empty title="No blueprint yet" hint="Design the project to produce one." />;
  }
  return (
    <div className="grid gap-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium">{blueprint.name}</h2>
            <p className="text-xs text-muted">{blueprint.tagline}</p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="h-5 w-5 rounded-full border border-hairline"
              style={{ background: blueprint.theme.brand }}
              title={blueprint.theme.brand}
            />
            <Badge>{blueprint.theme.mode}</Badge>
          </div>
        </div>
        {blueprint.notes && <p className="mt-3 text-xs leading-relaxed text-muted">{blueprint.notes}</p>}
      </Card>

      {blueprint.entities.map((e) => (
        <Card key={e.name} className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-5 py-3">
            <h3 className="text-sm font-medium">{e.name}</h3>
            <span className="font-mono text-2xs text-muted">/api/{e.route ?? ""}</span>
            {e.owned && <Badge tone="clay">owned</Badge>}
            {e.soft_delete && <Badge>soft delete</Badge>}
          </div>
          {e.description && <p className="px-5 pt-3 text-xs text-muted">{e.description}</p>}
          <div className="overflow-x-auto p-5 pt-3">
            <table className="w-full text-xs">
              <thead className="text-left text-muted">
                <tr>
                  <th className="pb-2 pr-4 font-medium">Field</th>
                  <th className="pb-2 pr-4 font-medium">Type</th>
                  <th className="pb-2 font-medium">Required</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {e.fields.map((f) => (
                  <tr key={f.name} className="border-t border-hairline/60">
                    <td className="py-1.5 pr-4">{f.name}</td>
                    <td className="py-1.5 pr-4 text-muted">
                      {f.type}
                      {f.enum ? ` (${f.enum})` : ""}
                    </td>
                    <td className="py-1.5 text-muted">{f.required ? "yes" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── Build ───────────────────────────────────────────────────────────────────

function BuildTab({
  builds, liveLines, liveBuildID, running,
}: {
  builds: Build[];
  liveLines: string[];
  liveBuildID: string | null;
  running: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const current = selected ?? liveBuildID ?? builds[0]?.id ?? null;
  const currentStatus = builds.find((b) => b.id === current)?.status;

  const { data: storedLog } = useQuery({
    queryKey: ["build-log", current, currentStatus],
    queryFn: () => api.builds.log(current!),
    // The STATUS is part of the key on purpose. Without it the log is fetched
    // once — possibly while the build is still queued and the stored log is
    // empty — and that empty answer is cached for the life of the page, so
    // the log stays blank even after the build finishes.
    enabled: !!current,
  });

  // Prefer whichever is actually populated, rather than switching on `running`.
  //
  // Switching on `running` had a visible failure: the instant a build
  // finished, `running` flipped false and the display swapped from the live
  // lines to a stored log that had not been refetched yet — so a completed
  // build showed an empty log where a moment earlier it had shown everything.
  const stored = (storedLog ?? "").trim();
  const lines = stored ? stored.split("\n") : liveLines;

  const box = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (running && box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [lines.length, running]);

  if (builds.length === 0 && !running) {
    return <Empty title="No builds yet" hint="Press Build to scaffold, fill and verify this project." />;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[15rem_1fr]">
      <div className="flex flex-col gap-2">
        {builds.map((b) => (
          <button
            key={b.id}
            onClick={() => setSelected(b.id)}
            className={`rounded-card border px-3 py-2 text-left transition ${
              current === b.id ? "border-clay" : "border-hairline hover:border-clay/50"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium tabular-nums">v{b.version}</span>
              <Status value={b.status} />
            </div>
            <span className="mt-0.5 block text-2xs text-muted">
              {relative(b.created_at)} · {duration(b.started_at, b.finished_at)}
            </span>
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-hairline px-4 py-2">
          <span className="text-xs text-muted">Build log</span>
          {running && (
            <span className="flex items-center gap-1.5 text-2xs text-clay">
              <span className="live-dot h-1.5 w-1.5 rounded-full bg-clay" />
              running
            </span>
          )}
        </div>
        <pre ref={box} className="logbox max-h-[32rem] overflow-y-auto px-4 py-3">
          {lines.length === 0 || (lines.length === 1 && !lines[0])
            ? "No output yet."
            : lines.join("\n")}
        </pre>
      </Card>
    </div>
  );
}

// ─── Files ───────────────────────────────────────────────────────────────────

function FilesTab({ buildID, slug, version }: { buildID?: string; slug: string; version?: number }) {
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["files", buildID],
    queryFn: () => api.builds.files(buildID!),
    enabled: !!buildID,
  });
  const { data: file } = useQuery({
    queryKey: ["file", buildID, open],
    queryFn: () => api.builds.file(buildID!, open!),
    enabled: !!buildID && !!open,
  });

  if (!buildID) return <Empty title="No files yet" hint="Build the project to generate its source." />;
  if (isLoading) return <Spinner />;
  if (!data) return null;

  const bySurface = data.items.reduce<Record<string, typeof data.items>>((acc, f) => {
    (acc[f.surface] ??= []).push(f);
    return acc;
  }, {});

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          {data.total} files · {data.items.filter((f) => f.origin === "ai").length} written by the model
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void downloadBuild(buildID, `${slug}-v${version ?? 1}.zip`)}
        >
          Download source
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <div className="max-h-[34rem] overflow-y-auto rounded-card border border-hairline">
          {Object.keys(bySurface).sort().map((surface) => (
            <div key={surface}>
              <p className="sticky top-0 bg-raised px-3 py-1.5 text-2xs uppercase tracking-wide text-muted">
                {surface}
              </p>
              {bySurface[surface].map((f) => (
                <button
                  key={f.path}
                  onClick={() => setOpen(f.path)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left font-mono text-2xs transition hover:bg-hairline/40 ${
                    open === f.path ? "bg-hairline/60" : ""
                  }`}
                >
                  <span className="truncate">{f.path}</span>
                  <span className="shrink-0 text-muted">
                    {f.origin === "ai" ? <span className="text-clay">ai</span> : bytes(f.bytes)}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>

        <Card className="overflow-hidden">
          {!open ? (
            <div className="flex h-full items-center justify-center px-6 py-16 text-center text-sm text-muted">
              Pick a file to read it.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-hairline px-4 py-2">
                <span className="truncate font-mono text-2xs">{open}</span>
                {file && (
                  <Badge tone={file.origin === "ai" ? "clay" : "muted"}>
                    {file.origin === "ai" ? "written by the model" : "chassis"}
                  </Badge>
                )}
              </div>
              <pre className="logbox max-h-[30rem] overflow-y-auto px-4 py-3">
                {file?.contents ?? "Loading…"}
              </pre>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── Deploys ─────────────────────────────────────────────────────────────────

function DeploysTab({
  projectID, deployments,
}: {
  projectID: string;
  deployments: import("../lib/types").Deployment[];
}) {
  const qc = useQueryClient();
  const rollback = useMutation({
    mutationFn: (id: string) => api.deployments.rollback(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectID] }),
  });

  if (deployments.length === 0) {
    return (
      <Empty
        title="Never deployed"
        hint="A deploy provisions the database, ships the binary to the fleet, waits for /health, and rolls itself back if it does not answer."
      />
    );
  }

  return (
    <div className="grid gap-3">
      {rollback.error && <ErrorNote error={rollback.error} />}
      {deployments.map((d) => (
        <Card key={d.id} className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Status value={d.status} />
                <span className="font-mono text-2xs text-muted">{d.release_id || "no release"}</span>
                {d.rolled_back_to && <Badge>rolled back to {d.rolled_back_to}</Badge>}
              </div>
              {d.url && <p className="mt-1 font-mono text-2xs text-moss">{d.url}</p>}
              {d.error && <p className="mt-1 max-w-2xl text-xs text-rust">{d.error}</p>}
              <p className="mt-1 text-2xs text-muted">
                {dateTime(d.created_at)} · {duration(d.started_at, d.finished_at)}
              </p>
            </div>
            {d.release_id && d.status !== "live" && (
              <Button
                size="sm"
                variant="ghost"
                disabled={rollback.isPending}
                onClick={() => rollback.mutate(d.id)}
              >
                Roll back to this
              </Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── Secrets ─────────────────────────────────────────────────────────────────

function SecretsTab({ projectID }: { projectID: string }) {
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");

  const { data } = useQuery({
    queryKey: ["secrets", projectID],
    queryFn: () => api.projects.secrets(projectID),
  });
  const put = useMutation({
    mutationFn: () => api.projects.putSecret(projectID, key, value),
    onSuccess: () => {
      setKey("");
      setValue("");
      void qc.invalidateQueries({ queryKey: ["secrets", projectID] });
    },
  });
  const del = useMutation({
    mutationFn: (k: string) => api.projects.deleteSecret(projectID, k),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["secrets", projectID] }),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-5">
        <h2 className="mb-1 text-sm font-medium">Environment secrets</h2>
        <p className="mb-4 text-xs leading-relaxed text-muted">
          Sealed at rest and written into the app's environment file on the next deploy. They are
          write-only — this console can show that one exists, never read it back.
        </p>
        {put.error && (
          <div className="mb-3">
            <ErrorNote error={put.error} />
          </div>
        )}
        <div className="flex flex-col gap-3">
          <Field label="Name" hint="UPPER_SNAKE_CASE — it becomes an environment variable.">
            <Input value={key} onChange={(v) => setKey(v.toUpperCase())} placeholder="STRIPE_SECRET_KEY" mono />
          </Field>
          <Field label="Value">
            <Input value={value} onChange={setValue} type="password" placeholder="sk_live_…" mono />
          </Field>
          <Button onClick={() => put.mutate()} disabled={!key || !value || put.isPending}>
            {put.isPending ? "Saving…" : "Save secret"}
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 text-sm font-medium">Set</h2>
        {!data || data.items.length === 0 ? (
          <p className="text-sm text-muted">None yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.items.map((s) => (
              <li key={s.key} className="flex items-center justify-between gap-3 border-b border-hairline/60 pb-2 last:border-0">
                <div className="min-w-0">
                  <p className="truncate font-mono text-xs">{s.key}</p>
                  <p className="font-mono text-2xs text-muted">{s.hint}</p>
                </div>
                <Button size="sm" variant="quiet" onClick={() => del.mutate(s.key)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4">
          <SimulatedNote>
            DATABASE_URL, PORT and JWT_SECRET are managed by Atelier and cannot be set here — an app pointed
            at a database Atelier did not provision could reach another tenant's.
          </SimulatedNote>
        </div>
      </Card>
    </div>
  );
}
